export class UnauthorizedError extends Error {
    status: number;

    constructor(status = 401) {
        super("Nicht autorisiert");
        this.name = "UnauthorizedError";
        this.status = status;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class ApplicationError extends Error {
    status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = "ApplicationError";
        this.status = status;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/**
 * The server refused an upload (413) because the file exceeds its size limit.
 * Carries the limit the server reported so the UI can name it in a localized
 * message rather than echoing the server's English one.
 */
export class FileTooLargeError extends Error {
    status = 413;
    maxFileSizeBytes: number;

    constructor(maxFileSizeBytes: number) {
        super("Datei zu groß");
        this.name = "FileTooLargeError";
        this.maxFileSizeBytes = maxFileSizeBytes;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/**
 * Completing a process task lost its connection before the server answered — a dropped network,
 * an aborted request, or a gateway (502/503/504) giving up on a step that runs longer than it
 * waits. The server keeps going regardless: the step it started (an Odoo lead, ~60 s on Prod) still
 * finishes and writes its message. So this is not a failure to report but a run to follow.
 */
export class TaskCompletionInterruptedError extends Error {
    constructor(cause?: unknown) {
        super("Verbindung unterbrochen, der Prozessschritt läuft weiter");
        this.name = "TaskCompletionInterruptedError";
        (this as any).cause = cause;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/**
 * The server refused to create a search profile or a user because the customer's tender plan
 * allows no more of them (HTTP 409, `error: "TENDER_PLAN_LIMIT"`). Carries the server's numbers
 * so the UI can say which limit, how many are used, how many are included and in which tier —
 * a generic "error" would leave the user guessing why a valid form was turned away.
 */
export type TenderPlanLimitKind = "profiles" | "users";

export class TenderPlanLimitError extends Error {
    status = 409;
    limit: TenderPlanLimitKind;
    included: number | null;
    used: number;
    tier: string | null;

    constructor(limit: TenderPlanLimitKind, included: number | null, used: number, tier: string | null) {
        super("TENDER_PLAN_LIMIT");
        this.name = "TenderPlanLimitError";
        this.limit = limit;
        this.included = included;
        this.used = used;
        this.tier = tier;
        Object.setPrototypeOf(this, new.target.prototype);
    }

    /** The error for a refusal body, or null when the body is something else. */
    static fromBody(body: any): TenderPlanLimitError | null {
        if (!body || body.error !== "TENDER_PLAN_LIMIT") return null;
        const limit: TenderPlanLimitKind = body.limit === "users" ? "users" : "profiles";
        const included = typeof body.included === "number" ? body.included : null;
        const used = typeof body.used === "number" ? body.used : 0;
        const tier = typeof body.tier === "string" ? body.tier : null;
        return new TenderPlanLimitError(limit, included, used, tier);
    }

    /** Reads a 409 response's body (from a clone, so the caller can still read it); null for any
     *  other status or body. */
    static async fromResponse(response: Response): Promise<TenderPlanLimitError | null> {
        if (response.status !== 409) return null;
        const body = await response.clone().json().catch(() => null);
        return TenderPlanLimitError.fromBody(body);
    }
}
